(() => {
  class ProductShowcasePro {
    constructor(section) {
      this.el = section;
      this.isCarousel = section.classList.contains('psp--carousel');
      this.cards = Array.from(section.querySelectorAll('[data-product-card]'));
      this.filters = section.querySelectorAll('.psp-filter');
      this.overlay = section.querySelector('[data-psp-popup-overlay]');
      this.popup = section.querySelector('[data-psp-popup]');
      this.popupClose = section.querySelector('[data-psp-popup-close]');
      this.popupContent = section.querySelector('[data-psp-popup-content]');
      
      if (this.isCarousel) {
        this.track = section.querySelector('[data-psp-carousel-track]');
        this.progress = section.querySelector('[data-psp-carousel-progress]');
      }
      
      if (!this.cards.length) return;
      
      this.state = {
        isMobile: false,
        activeFilter: 'all',
        activePopup: null,
        isPopupVisible: false,
        hoverTimers: new Map(),
        autoHoverTimers: new Map(),
        intersectionObserver: null,
        cardInViewport: new Set(),
        abort: new AbortController(),
        seeMoreEnabled: this.el.dataset.seeMoreEnabled === 'true',
        isClosingPopup: false,
        savedScrollPosition: 0,
        savedBodyStyles: {}
      };
      
      this.init();
    }
    
    init() {
      this.updateMode();
      this.bindEvents();
      this.initFilters();
      this.initReveal();
      this.initPopup();
      
      if (this.isCarousel) {
        this.bindCarouselEvents();
        this.updateProgress();
      }
      
      if (this.state.isMobile) {
        this.setupIntersectionObserver();
        this.setupAutoHoverForVisibleCards();
      }
    }
    
    updateMode() {
      this.state.isMobile = window.matchMedia('(max-width: 768px)').matches;
      this.el.classList.toggle('is-mobile', this.state.isMobile);
      this.el.classList.toggle('is-desktop', !this.state.isMobile);
      
      if (this.isCarousel) {
        this.track.style.scrollSnapType = this.state.isMobile ? 'x mandatory' : 'none';
      }
    }
    
    bindEvents() {
      const { signal } = this.state.abort;
      
      this.el.addEventListener('click', (e) => {
        let target = e.target;
        
        while (target && target !== this.el) {
          if (target.classList && target.classList.contains('psp-see-more') && 
              target.hasAttribute('data-psp-see-more')) {
            e.preventDefault();
            e.stopPropagation();
            
            const productId = target.dataset.productId;
            if (productId) {
              this.showPopup(productId);
            }
            return;
          }
          target = target.parentElement;
        }
        
        const copyBtn = e.target.closest('.psp-promo-copy');
        if (copyBtn) {
          e.preventDefault();
          e.stopPropagation();
          
          const promoCode = copyBtn.dataset.promoCode;
          this.copyToClipboard(promoCode, copyBtn);
        }
      }, { signal });
      
      window.addEventListener('resize', () => {
        const wasMobile = this.state.isMobile;
        this.updateMode();
        
        if (this.isCarousel) {
          this.updateProgress();
        }
        
        if (wasMobile !== this.state.isMobile) {
          this.clearAllHoverTimers();
          
          if (this.state.intersectionObserver) {
            this.state.intersectionObserver.disconnect();
            this.state.intersectionObserver = null;
          }
          
          this.state.cardInViewport.clear();
          
          if (this.state.isMobile) {
            this.setupIntersectionObserver();
            this.setupAutoHoverForVisibleCards();
          }
        }
      }, { signal });
      
      this.popupClose.addEventListener('click', (e) => {
        e.stopPropagation();
        this.hidePopup();
      }, { signal });
      
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay && !this.state.isClosingPopup) {
          this.hidePopup();
        }
      }, { signal });
      
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.state.isPopupVisible && !this.state.isClosingPopup) {
          this.hidePopup();
        }
      }, { signal });
    }
    
    saveBodyScrollPosition() {
      this.state.savedScrollPosition = window.pageYOffset || 
                                       document.documentElement.scrollTop || 
                                       document.body.scrollTop || 0;
      
      this.state.savedBodyStyles = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        top: document.body.style.top,
        left: document.body.style.left,
        right: document.body.style.right,
        width: document.body.style.width,
        height: document.body.style.height
      };
      
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.state.savedScrollPosition}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.height = '100%';
    }
    
    restoreBodyScrollPosition() {
      Object.keys(this.state.savedBodyStyles).forEach(key => {
        document.body.style[key] = this.state.savedBodyStyles[key];
      });
      
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
      
      window.scrollTo(0, this.state.savedScrollPosition);
      
      this.state.savedScrollPosition = 0;
      this.state.savedBodyStyles = {};
    }
    
    initFilters() {
      this.filters.forEach(btn => {
        btn.addEventListener('click', () => {
          this.filters.forEach(b => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          this.applyFilter(btn.dataset.filter);
        }, { signal: this.state.abort.signal });
      });
    }
    
    applyFilter(key) {
      this.state.activeFilter = key;
      
      this.cards.forEach(card => {
        const labels = (card.dataset.labels || '')
          .split(',')
          .map(l => l.trim())
          .filter(l => l !== '');
        
        const shouldShow = key === 'all' || labels.includes(key);
        card.style.display = shouldShow ? '' : 'none';
      });
      
      if (this.isCarousel) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
      }
      
      this.clearAllHoverTimers();
      
      if (this.state.isMobile) {
        setTimeout(() => {
          this.setupAutoHoverForVisibleCards();
        }, 300);
      }
    }
    
    bindCarouselEvents() {
      const { signal } = this.state.abort;
      
      this.track.addEventListener('scroll', () => {
        this.updateProgress();
        this.state.isScrolling = true;
        clearTimeout(this.state.scrollEndTimer);
        this.state.scrollEndTimer = setTimeout(() => {
          this.state.isScrolling = false;
        }, 100);
      }, { signal });
      
      this.el.addEventListener('wheel', e => this.onWheel(e), { passive: false, signal });
      
      this.track.addEventListener('mousedown', e => this.onDown(e), { signal });
      window.addEventListener('mousemove', e => this.onMove(e), { signal });
      window.addEventListener('mouseup', () => this.onUp(), { signal });
      
      this.track.addEventListener('touchstart', e => this.onTouchStart(e), { passive: true, signal });
      this.track.addEventListener('touchmove', e => this.onTouchMove(e), { passive: false, signal });
      this.track.addEventListener('touchend', e => this.onTouchEnd(e), { signal });
    }
    
    onWheel(e) {
      if (this.state.isMobile) return;
      
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max <= 0) return;
      
      const atStart = this.track.scrollLeft <= 0;
      const atEnd = this.track.scrollLeft >= max - 1;
      
      if ((e.deltaY < 0 && atStart) || (e.deltaY > 0 && atEnd)) return;
      
      e.preventDefault();
      this.track.scrollLeft += e.deltaY * 1.1;
    }
    
    onDown(e) {
      if (this.state.isMobile) return;
      
      this.state.carouselState = {
        isDown: true,
        startX: e.pageX,
        scrollStart: this.track.scrollLeft,
        lastX: e.pageX,
        lastTime: performance.now(),
        velocity: 0
      };
      
      cancelAnimationFrame(this.state.carouselRaf);
      this.clearAllHoverTimers();
    }
    
    onMove(e) {
      const p = e.touches ? e.touches[0] : e;
      
      if (!this.state.isMobile && this.state.carouselState?.isDown) {
        const delta = p.pageX - this.state.carouselState.startX;
        this.track.scrollLeft = this.state.carouselState.scrollStart - delta;
        
        const now = performance.now();
        const dx = p.pageX - this.state.carouselState.lastX;
        const dt = now - this.state.carouselState.lastTime;
        
        if (dt > 0) {
          this.state.carouselState.velocity = dx / dt;
          this.state.carouselState.lastX = p.pageX;
          this.state.carouselState.lastTime = now;
        }
        
        if (e.cancelable) e.preventDefault();
      }
    }
    
    onUp() {
      if (!this.state.isMobile && this.state.carouselState?.isDown) {
        this.state.carouselState.isDown = false;
        this.applyInertia();
      }
    }
    
    applyInertia() {
      let v = this.state.carouselState.velocity * 32;
      
      const step = () => {
        v *= 0.92;
        this.track.scrollLeft -= v;
        
        if (Math.abs(v) > 0.4) {
          this.state.carouselRaf = requestAnimationFrame(step);
        }
      };
      
      step();
    }
    
    onTouchStart(e) {
      if (!this.state.isMobile) return;
      
      const card = e.target.closest('[data-product-card]');
      if (!card) return;
      
      this.stopAutoHoverForCard(card);
      this.deactivateHover(card);
      this.clearCardHoverTimers(card);
      
      this.state.holdStartTime = Date.now();
      this.state.activeHoldCard = card;
      
      const holdTimer = setTimeout(() => {
        if (this.state.activeHoldCard === card) {
          this.activateHover(card);
        }
      }, 1250);
      
      if (!this.state.hoverTimers.has(card)) {
        this.state.hoverTimers.set(card, {});
      }
      this.state.hoverTimers.get(card).holdTimer = holdTimer;
    }
    
    onTouchMove(e) {
      if (!this.state.isMobile) return;
      
      const touch = e.touches[0];
      const card = document.elementFromPoint(touch.clientX, touch.clientY)
        ?.closest('[data-product-card]');
      
      if (card && this.state.activeHoldCard && card !== this.state.activeHoldCard) {
        this.clearCardHoverTimers(this.state.activeHoldCard);
        this.state.activeHoldCard = card;
      }
    }
    
    onTouchEnd(e) {
      if (!this.state.isMobile) return;
      
      const card = this.state.activeHoldCard;
      if (!card) return;
      
      this.clearCardHoverTimers(card, 'holdTimer');
      
      const holdDuration = Date.now() - this.state.holdStartTime;
      
      if (holdDuration >= 1250) {
        const resetTimer = setTimeout(() => {
          this.deactivateHover(card);
          
          if (this.state.cardInViewport.has(card)) {
            setTimeout(() => {
              this.startAutoHoverForCard(card);
            }, 1500);
          }
        }, 50);
        
        if (!this.state.hoverTimers.has(card)) {
          this.state.hoverTimers.set(card, {});
        }
        this.state.hoverTimers.get(card).resetTimer = resetTimer;
      } else {
        this.deactivateHover(card);
        
        if (this.state.cardInViewport.has(card)) {
          this.startAutoHoverForCard(card);
        }
      }
      
      this.state.activeHoldCard = null;
    }
    
    updateProgress() {
      if (!this.progress || !this.isCarousel) return;
      
      const max = this.track.scrollWidth - this.track.clientWidth;
      const ratio = max > 0 ? this.track.scrollLeft / max : 0;
      
      this.progress.style.transform = `scaleX(${Math.min(1, Math.max(0, ratio))})`;
    }
    
    setupIntersectionObserver() {
      if (!this.state.isMobile) return;
      
      if (this.state.intersectionObserver) {
        this.state.intersectionObserver.disconnect();
      }
      
      this.state.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            const card = entry.target;
            
            if (entry.isIntersecting) {
              this.state.cardInViewport.add(card);
              this.startAutoHoverForCard(card);
            } else {
              this.state.cardInViewport.delete(card);
              this.stopAutoHoverForCard(card);
              this.deactivateHover(card);
            }
          });
        },
        {
          threshold: 0.3,
          rootMargin: '50px 0px 50px 0px'
        }
      );
      
      this.cards.forEach(card => {
        this.state.intersectionObserver.observe(card);
      });
    }
    
    startAutoHoverForCard(card) {
      this.stopAutoHoverForCard(card);
      
      if (!this.state.cardInViewport.has(card)) return;
      
      const startHoverLoop = () => {
        const waitTimer = setTimeout(() => {
          if (!this.state.cardInViewport.has(card)) return;
          
          this.activateHover(card);
          
          const resetTimer = setTimeout(() => {
            this.deactivateHover(card);
            
            if (this.state.cardInViewport.has(card)) {
              startHoverLoop();
            }
          }, 2000);
          
          if (!this.state.autoHoverTimers.has(card)) {
            this.state.autoHoverTimers.set(card, {});
          }
          this.state.autoHoverTimers.get(card).resetTimer = resetTimer;
          
        }, 1500);
        
        if (!this.state.autoHoverTimers.has(card)) {
          this.state.autoHoverTimers.set(card, {});
        }
        this.state.autoHoverTimers.get(card).waitTimer = waitTimer;
      };
      
      startHoverLoop();
    }
    
    stopAutoHoverForCard(card) {
      const timers = this.state.autoHoverTimers.get(card);
      if (timers) {
        if (timers.waitTimer) clearTimeout(timers.waitTimer);
        if (timers.resetTimer) clearTimeout(timers.resetTimer);
        this.state.autoHoverTimers.delete(card);
      }
    }
    
    setupAutoHoverForVisibleCards() {
      this.state.autoHoverTimers.forEach((timers, card) => {
        if (timers.waitTimer) clearTimeout(timers.waitTimer);
        if (timers.resetTimer) clearTimeout(timers.resetTimer);
      });
      this.state.autoHoverTimers.clear();
      
      this.cards.forEach(card => {
        if (this.state.cardInViewport.has(card)) {
          this.startAutoHoverForCard(card);
        }
      });
    }
    
    activateHover(card) {
      if (!card) return;
      
      this.cards.forEach(c => {
        if (c !== card) {
          c.classList.remove('is-hover-preview');
        }
      });
      
      card.classList.add('is-hover-preview');
    }
    
    deactivateHover(card) {
      if (card) {
        card.classList.remove('is-hover-preview');
      } else {
        this.cards.forEach(c => c.classList.remove('is-hover-preview'));
      }
    }
    
    clearCardHoverTimers(card, timerType = 'all') {
      if (!this.state.hoverTimers.has(card)) return;
      
      const timers = this.state.hoverTimers.get(card);
      
      if (timerType === 'all') {
        if (timers.holdTimer) clearTimeout(timers.holdTimer);
        if (timers.resetTimer) clearTimeout(timers.resetTimer);
        this.state.hoverTimers.delete(card);
      } else if (timers[timerType]) {
        clearTimeout(timers[timerType]);
        delete timers[timerType];
      }
    }
    
    clearAllHoverTimers() {
      this.state.hoverTimers.forEach((timers, card) => {
        if (timers.holdTimer) clearTimeout(timers.holdTimer);
        if (timers.resetTimer) clearTimeout(timers.resetTimer);
      });
      this.state.hoverTimers.clear();
      
      this.state.autoHoverTimers.forEach((timers, card) => {
        if (timers.waitTimer) clearTimeout(timers.waitTimer);
        if (timers.resetTimer) clearTimeout(timers.resetTimer);
      });
      this.state.autoHoverTimers.clear();
      
      this.deactivateHover();
    }
    
    copyToClipboard(text, button) {
      navigator.clipboard.writeText(text).then(() => {
        button.classList.add('copied');
        button.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M13 4L6 11L3 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
        
        setTimeout(() => {
          button.classList.remove('copied');
          button.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="4" y="4" width="8" height="8" rx="1" stroke="currentColor" stroke-width="1.2"/>
              <rect x="2" y="2" width="8" height="8" rx="1" fill="currentColor" fill-opacity="0.1" stroke="currentColor" stroke-width="1.2"/>
            </svg>
          `;
        }, 2000);
      }).catch(err => {
        console.error('Failed to copy: ', err);
      });
    }
    
    initPopup() {
      this.setupPopupGestures();
      
      const { signal } = this.state.abort;
      
      const popupObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            const isVisible = this.popup.classList.contains('is-visible');
            if (isVisible && !this.state.isPopupVisible) {
              this.state.isPopupVisible = true;
            }
          }
        });
      });
      
      popupObserver.observe(this.popup, {
        attributes: true,
        attributeFilter: ['class']
      });
      
      signal.addEventListener('abort', () => popupObserver.disconnect());
    }
    
    setupPopupGestures() {
      let startY = 0;
      let startX = 0;
      let currentY = 0;
      let isDragging = false;
      let isVerticalSwipe = false;
      let popupScrollTop = 0;
      
      const onTouchStart = (e) => {
        if (!this.state.isPopupVisible || this.state.isClosingPopup) return;
        
        const touch = e.touches[0];
        startY = touch.clientY;
        startX = touch.clientX;
        currentY = startY;
        isDragging = true;
        isVerticalSwipe = false;
        
        popupScrollTop = this.popupContent.scrollTop;
        this.state.popupIsScrolling = popupScrollTop > 0;
        
        this.popup.classList.add('is-dragging');
        this.popup.style.transition = 'none';
        this.overlay.style.transition = 'none';
      };
      
      const onTouchMove = (e) => {
        if (!isDragging || !this.state.isPopupVisible || this.state.isClosingPopup) return;
        
        const touch = e.touches[0];
        currentY = touch.clientY;
        const deltaY = currentY - startY;
        const deltaX = touch.clientX - startX;
        
        if (!isVerticalSwipe && Math.abs(deltaY) > Math.abs(deltaX)) {
          isVerticalSwipe = true;
        }
        
        if (isVerticalSwipe) {
          if (popupScrollTop === 0 && deltaY > 0) {
            e.preventDefault();
            
            const translateY = Math.min(deltaY, 200);
            const scale = 1 - (translateY / 800);
            const opacity = 1 - (translateY / 300);
            const blur = Math.min(12, translateY / 20);
            
            if (this.state.isMobile && this.el.dataset.popupFullscreenMobile === 'false') {
              this.popup.style.transform = `translate(-50%, ${translateY}px)`;
            } else {
              this.popup.style.transform = `translate(-50%, calc(-50% + ${translateY}px)) scale(${scale})`;
              this.popup.style.opacity = opacity;
            }
            
            if (this.state.popupHeaderImage) {
              const headerScale = 1 + (translateY / 1000);
              this.state.popupHeaderImage.style.transform = `scale(${headerScale})`;
            }
            
            this.overlay.style.backdropFilter = `blur(${Math.max(0, 12 - blur)}px)`;
            this.overlay.style.webkitBackdropFilter = `blur(${Math.max(0, 12 - blur)}px)`;
          } else if (deltaY < 0) {
            this.state.popupIsScrolling = true;
          }
        }
      };
      
      const onTouchEnd = () => {
        if (!isDragging || !this.state.isPopupVisible || this.state.isClosingPopup) return;
        
        isDragging = false;
        this.popup.classList.remove('is-dragging');
        this.popup.style.transition = 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.3s ease';
        this.overlay.style.transition = 'backdrop-filter 0.3s ease, -webkit-backdrop-filter 0.3s ease';
        
        const deltaY = currentY - startY;
        const velocity = deltaY / 16;
        
        const threshold = 80;
        const velocityThreshold = 2;
        
        if ((deltaY > threshold || velocity > velocityThreshold) && isVerticalSwipe && !this.state.popupIsScrolling) {
          this.hidePopup();
        } else {
          if (this.state.isMobile && this.el.dataset.popupFullscreenMobile === 'false') {
            this.popup.style.transform = 'translate(-50%, 0)';
          } else {
            this.popup.style.transform = 'translate(-50%, -50%) scale(1)';
            this.popup.style.opacity = '1';
          }
          
          if (this.state.popupHeaderImage) {
            this.state.popupHeaderImage.style.transform = 'scale(1)';
          }
          
          this.overlay.style.backdropFilter = 'blur(12px)';
          this.overlay.style.webkitBackdropFilter = 'blur(12px)';
        }
        
        this.state.popupIsScrolling = false;
      };
      
      this.popup.addEventListener('touchstart', onTouchStart, { passive: true });
      this.popup.addEventListener('touchmove', onTouchMove, { passive: false });
      this.popup.addEventListener('touchend', onTouchEnd);
    }
    
    async showPopup(productId) {
      if (this.state.isPopupVisible || this.state.isClosingPopup) {
        return;
      }
      
      this.saveBodyScrollPosition();
      
      this.state.activePopup = productId;
      
      const card = this.cards.find(c => c.dataset.productId === productId);
      if (!card) {
        console.error('Card not found for productId:', productId);
        return;
      }
      
      try {
        const popupDataStr = card.dataset.popupData;
        
        let popupData;
        try {
          popupData = JSON.parse(popupDataStr);
        } catch (jsonError) {
          console.error('JSON parse error:', jsonError);
          const fixedStr = popupDataStr
            .replace(/\\'/g, "'")
            .replace(/\\"/g, '"')
            .replace(/\n/g, '\\n')
            .replace(/\r/g, '\\r');
          
          popupData = JSON.parse(fixedStr);
        }
        
        const content = this.buildPopupContent({
          title: popupData.title || card.querySelector('.psp-card-title a')?.textContent || 'Product Details',
          url: popupData.button_url || card.querySelector('.psp-card-title a')?.href || '#',
          image: popupData.image || card.querySelector('.psp-img-main')?.src,
          header_image: popupData.header_image || popupData.image || card.querySelector('.psp-img-main')?.src,
          description: popupData.description || '',
          features: popupData.features || [],
          cta_heading: popupData.cta_heading,
          cta_text: popupData.cta_text,
          button_text: popupData.button_text || 'Shop Now',
          icon_list_heading: popupData.icon_list_heading || '',
          icon_list_alignment: popupData.icon_list_alignment || 'alternate',
          icon_list_media_size: popupData.icon_list_media_size || 'medium',
          popup_logo: popupData.popup_logo,
          popup_logo_max_width: popupData.popup_logo_max_width,
          popup_logo_opacity: popupData.popup_logo_opacity,
          productId: productId
        });
        
        this.popupContent.innerHTML = content;
        this.popup.setAttribute('data-fullscreen-mobile', this.el.dataset.popupFullscreenMobile);
        
        this.applyPopupStyles();
        
        requestAnimationFrame(() => {
          this.state.isPopupVisible = true;
          this.state.isClosingPopup = false;
          this.overlay.classList.add('is-visible');
          this.popup.classList.add('is-visible');
          
          this.popupClose.focus();
          
          setTimeout(() => {
            this.loadPopupMedia();
            this.setupPopupParallax();
            this.setupPopupEvents();
          }, 100);
        });
        
      } catch (error) {
        console.error('Error loading popup data:', error);
        this.popupContent.innerHTML = `
          <div class="psp-popup-content-inner">
            <h2 class="psp-popup-heading">Product Details</h2>
            <div class="psp-popup-description">
              <p>Unable to load product details. Please try again.</p>
            </div>
            <div class="psp-popup-actions-container">
              <div class="psp-popup-actions">
                <a href="#" class="psp-popup-button psp-popup-button-primary">Shop Now</a>
              </div>
            </div>
          </div>
        `;
        
        this.applyPopupStyles();
        
        this.state.isPopupVisible = true;
        this.state.isClosingPopup = false;
        this.overlay.classList.add('is-visible');
        this.popup.classList.add('is-visible');
      }
    }
    
    applyPopupStyles() {
      this.popup.style.cssText = `
        --popup-overlay-bg: ${getComputedStyle(this.el).getPropertyValue('--popup-overlay-bg')};
        --popup-overlay-opacity: ${getComputedStyle(this.el).getPropertyValue('--popup-overlay-opacity')};
        --popup-bg: ${getComputedStyle(this.el).getPropertyValue('--popup-bg')};
        --popup-radius: ${getComputedStyle(this.el).getPropertyValue('--popup-radius')};
        --popup-padding: ${getComputedStyle(this.el).getPropertyValue('--popup-padding')};
        --popup-top-padding: ${getComputedStyle(this.el).getPropertyValue('--popup-top-padding')};
        --popup-bottom-padding: ${getComputedStyle(this.el).getPropertyValue('--popup-bottom-padding')};
        --popup-max-width: ${getComputedStyle(this.el).getPropertyValue('--popup-max-width')};
        --popup-max-height: ${getComputedStyle(this.el).getPropertyValue('--popup-max-height') || '85vh'};
        --popup-media-ratio: ${getComputedStyle(this.el).getPropertyValue('--popup-media-ratio')};
        --popup-heading-color: ${getComputedStyle(this.el).getPropertyValue('--popup-heading-color')};
        --popup-heading-size: ${getComputedStyle(this.el).getPropertyValue('--popup-heading-size')};
        --popup-text-color: ${getComputedStyle(this.el).getPropertyValue('--popup-text-color')};
        --popup-text-size: ${getComputedStyle(this.el).getPropertyValue('--popup-text-size')};
        --popup-header-height: ${getComputedStyle(this.el).getPropertyValue('--popup-header-height') || '300px'};
      `;
      
      this.popup.style.paddingTop = `var(--popup-top-padding, 40px)`;
      this.popup.style.paddingBottom = `var(--popup-bottom-padding, 40px)`;
      this.popup.style.paddingLeft = `var(--popup-padding, 32px)`;
      this.popup.style.paddingRight = `var(--popup-padding, 32px)`;
      this.popup.style.maxWidth = `var(--popup-max-width, 800px)`;
      this.popup.style.maxHeight = `var(--popup-max-height, 85vh)`;
      this.popup.style.borderRadius = `var(--popup-radius, 20px)`;
      this.popup.style.backgroundColor = `var(--popup-bg, #ffffff)`;
    }
    
    setupPopupParallax() {
      this.state.popupHeaderImage = this.popupContent.querySelector('.psp-popup-header-image');
    }
    
    loadPopupMedia() {
      const featureImages = this.popupContent.querySelectorAll('.psp-popup-feature-image img[data-src]');
      
      if (featureImages.length > 0 && 'IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              imageObserver.unobserve(img);
            }
          });
        }, {
          root: this.popupContent,
          threshold: 0.1
        });
        
        featureImages.forEach(img => imageObserver.observe(img));
      }
      
      const featureVideos = this.popupContent.querySelectorAll('.psp-popup-feature-video video');
      featureVideos.forEach(video => {
        if (video.dataset.src) {
          video.src = video.dataset.src;
          video.removeAttribute('data-src');
          video.load();
        }
      });
    }
    
    buildPopupContent(data) {
      let headerHTML = '';
      if (data.header_image && data.header_image.trim() !== '' && !data.header_image.includes('no-image')) {
        headerHTML = `
          <div class="psp-popup-header">
            <img src="${data.header_image}" 
                 alt="${data.title || 'Product Image'}" 
                 class="psp-popup-header-image"
                 loading="eager"
                 onerror="this.style.display='none'; this.closest('.psp-popup-header').style.display='none';">
            <div class="psp-popup-header-overlay"></div>
          </div>
        `;
      }
      
      const featuresHTML = data.features && data.features.length > 0 ? `
        <div class="psp-popup-features">
          ${data.icon_list_heading ? `<h2 class="psp-popup-features-heading">${data.icon_list_heading}</h2>` : ''}
          <ul class="psp-popup-features-list">
            ${data.features
              .map((feature, index) => {
                if (!feature) return '';
                
                const hasImage = feature.image && feature.image.trim() !== '' && !feature.image.includes('no-image');
                const hasVideo = feature.type === 'video' && feature.video && feature.video.trim() !== '';
                const hasMedia = hasImage || hasVideo;
                
                const hasHeadline = feature.headline && feature.headline.trim() !== '';
                const hasText = feature.text && feature.text.trim() !== '';
                const hasContent = hasHeadline || hasText;
                
                if (!hasMedia && !hasContent) return '';
                
                let itemAlignment = feature.alignment === 'default' ? 
                                  data.icon_list_alignment || 'alternate' : 
                                  feature.alignment;
                
                let itemSize = feature.size === 'default' ? 
                              data.icon_list_media_size || 'medium' : 
                              feature.size;
                
                let mediaHTML = '';
                if (hasMedia) {
                  if (hasVideo) {
                    mediaHTML = `
                      <div class="psp-popup-feature-video">
                        <video autoplay muted loop playsinline 
                               data-src="${feature.video}">
                          <source src="${feature.video}" type="video/mp4">
                        </video>
                      </div>
                    `;
                  } else if (hasImage) {
                    mediaHTML = `
                      <div class="psp-popup-feature-image">
                        <img src="${feature.image}" 
                             alt="${feature.headline || 'Feature image'}" 
                             loading="lazy"
                             onerror="this.style.display='none'; this.parentElement.style.display='none';">
                      </div>
                    `;
                  }
                }
                
                let alignmentClass = '';
                let textAlignmentClass = '';
                
                if (itemAlignment === 'alternate') {
                  alignmentClass = `media-align-${index % 2 === 0 ? 'left' : 'right'}`;
                  textAlignmentClass = 'text-align-left';
                } else if (itemAlignment === 'left' || itemAlignment === 'right') {
                  alignmentClass = `media-align-${itemAlignment}`;
                  textAlignmentClass = `text-align-${itemAlignment}`;
                }
                
                const sizeClass = hasMedia ? `media-size-${itemSize}` : '';
                const noMediaClass = !hasMedia ? 'no-media' : '';
                const contentAlignmentClass = !hasMedia ? textAlignmentClass : '';
                
                return `
                  <li class="psp-popup-feature-item 
                             ${hasMedia ? 'has-media' : 'no-media'} 
                             ${alignmentClass} 
                             ${sizeClass}
                             ${contentAlignmentClass}">
                    ${hasMedia ? `
                      <div class="psp-popup-feature-media">
                        ${mediaHTML}
                      </div>
                    ` : ''}
                    <div class="psp-popup-feature-content">
                      ${hasHeadline ? `<h4 class="psp-popup-feature-headline">${feature.headline}</h4>` : ''}
                      ${hasText ? `<div class="psp-popup-feature-text">${feature.text}</div>` : ''}
                    </div>
                  </li>
                `;
              })
              .filter(item => item !== '')
              .join('')}
          </ul>
        </div>
      ` : '';
      
      const ctaHTML = (data.cta_heading || data.cta_text) ? `
        <div class="psp-popup-cta">
          ${data.cta_heading ? `<h3 class="psp-popup-cta-heading">${data.cta_heading}</h3>` : ''}
          ${data.cta_text ? `<div class="psp-popup-cta-text">${data.cta_text}</div>` : ''}
        </div>
      ` : '';
      
      const logoHTML = data.popup_logo && 
                      data.popup_logo.trim() !== '' && 
                      !data.popup_logo.includes('no-image') ? `
        <div class="psp-popup-logo-container">
          <img src="${data.popup_logo}" 
               alt="Logo" 
               class="psp-popup-logo"
               style="
                 max-width: ${data.popup_logo_max_width || 120}px;
                 opacity: ${data.popup_logo_opacity || 0.7};
               "
               loading="lazy"
               onerror="this.style.display='none'; this.parentElement.style.display='none';">
        </div>
      ` : '';
      
      return `
        ${headerHTML}
        
        <div class="psp-popup-content-inner">
          <h2 class="psp-popup-heading">${data.title}</h2>
          
          <div class="psp-popup-description">
            ${data.description || '<p>Explore the premium features and exceptional quality of this product.</p>'}
          </div>
          
          ${featuresHTML}
          
          ${ctaHTML}
          
          ${logoHTML}
        </div>
        
        <div class="psp-popup-actions-container">
          <div class="psp-popup-actions">
            <a href="${data.url}" class="psp-popup-button psp-popup-button-primary">${data.button_text}</a>
          </div>
        </div>
      `;
    }
    
    setupPopupEvents() {
      const secondaryButtons = this.popupContent.querySelectorAll('.psp-popup-button-secondary');
      secondaryButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.hidePopup();
        });
      });
      
      const primaryButtons = this.popupContent.querySelectorAll('.psp-popup-button-primary');
      primaryButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          if (button.getAttribute('href') === '#') {
            e.preventDefault();
          }
        });
      });
    }
    
    hidePopup() {
      if (this.state.isClosingPopup) return;
      
      this.state.isClosingPopup = true;
      this.state.isPopupVisible = false;
      this.overlay.classList.remove('is-visible');
      this.popup.classList.remove('is-visible');
      this.state.activePopup = null;
      
      if (this.state.isMobile && this.el.dataset.popupFullscreenMobile === 'false') {
        this.popup.style.transform = 'translate(-50%, 100%)';
      } else {
        this.popup.style.transform = 'translate(-50%, -50%) scale(0.95)';
      }
      this.popup.style.opacity = '0';
      
      setTimeout(() => {
        this.restoreBodyScrollPosition();
        
        this.popupContent.innerHTML = '';
        this.state.popupScrollTop = 0;
        this.state.popupIsScrolling = false;
        this.state.popupHeaderImage = null;
        this.state.isClosingPopup = false;
        
        this.popup.style.cssText = '';
        this.applyPopupStyles();
      }, 300);
    }
    
    initReveal() {
      const io = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          });
        },
        { threshold: 0.2 }
      );
      
      this.cards.forEach(card => io.observe(card));
    }
    
    destroy() {
      this.state.abort.abort();
      
      if (this.state.carouselRaf) {
        cancelAnimationFrame(this.state.carouselRaf);
      }
      
      if (this.state.intersectionObserver) {
        this.state.intersectionObserver.disconnect();
        this.state.intersectionObserver = null;
      }
      
      this.clearAllHoverTimers();
      
      this.state.autoHoverTimers.forEach((timers, card) => {
        if (timers.waitTimer) clearTimeout(timers.waitTimer);
        if (timers.resetTimer) clearTimeout(timers.resetTimer);
      });
      this.state.autoHoverTimers.clear();
      
      this.state.cardInViewport.clear();
      
      if (this.state.isPopupVisible) {
        this.restoreBodyScrollPosition();
      }
    }
  }
  
  document.addEventListener('DOMContentLoaded', () => {
    document
      .querySelectorAll('[data-section-type="product-showcase-pro"]')
      .forEach(section => {
        const instance = new ProductShowcasePro(section);
        section.__psp = instance;
      });
  });
  
  document.addEventListener('shopify:section:unload', e => {
    const section = e.target;
    if (section?.__psp) section.__psp.destroy();
  });
})();